import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { FlightTypeEnum } from "../enum/flightType";

// Interface for individual flight leg
export interface FlightLeg {
  fromAirport: string;
  toAirport: string;
  departureDate: string;
  returnDate: string;
  passengers: number;
}

// Interface for airport data
export interface Airport {
  name: string;
  city: string;
  iata: string;
  icao: string;
  lat?: number;
  lng?: number;
}

// Interface for quote data (for persistence across page reloads)
export interface QuoteData {
  flightType: FlightTypeEnum;
  flights: FlightLeg[];
}

// Main store interface
interface FlightStore {
  // Flight search state
  flightType: FlightTypeEnum;
  flights: FlightLeg[];
  isSubmitting: boolean;

  // Airport data
  airports: Airport[];
  airportSuggestions: Airport[];

  // Quote data (persisted)
  quoteData: QuoteData | null;

  // Actions
  setFlightType: (type: FlightTypeEnum) => void;
  setFlights: (flights: FlightLeg[]) => void;
  addFlightLeg: () => void;
  updateFlightLeg: (
    index: number,
    field: keyof FlightLeg,
    value: string | number
  ) => void;
  removeFlightLeg: (index: number) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  setAirports: (airports: Airport[]) => void;
  setAirportSuggestions: (suggestions: Airport[]) => void;
  setQuoteData: (quoteData: QuoteData | null) => void;
  resetForm: () => void;
  loadPersistedQuote: () => void;
}

// Default flight leg
const defaultFlightLeg: FlightLeg = {
  fromAirport: "",
  toAirport: "",
  departureDate: "",
  returnDate: "",
  passengers: 1,
};

// Default state
const defaultState = {
  flightType: FlightTypeEnum.ONEWAY,
  flights: [defaultFlightLeg],
  isSubmitting: false,
  airports: [],
  airportSuggestions: [],
  quoteData: null,
};

export const useFlightStore = create<FlightStore>()(
  persist(
    (set, get) => ({
      ...defaultState,

      setFlightType: (type: FlightTypeEnum) => {
        set({ flightType: type });
        // Reset flights when changing flight type
        set({
          flights: [defaultFlightLeg],
          quoteData: {
            flightType: type,
            flights: [defaultFlightLeg],
          },
        });
      },

      setFlights: (flights: FlightLeg[]) => {
        const { flightType } = get();
        set({
          flights,
          quoteData: {
            flightType,
            flights,
          },
        });
      },

      addFlightLeg: () => {
        const { flights, flightType } = get();
        const newFlights = [...flights, { ...defaultFlightLeg }];
        set({
          flights: newFlights,
          quoteData: {
            flightType,
            flights: newFlights,
          },
        });
      },

      updateFlightLeg: (
        index: number,
        field: keyof FlightLeg,
        value: string | number
      ) => {
        const { flights, flightType } = get();
        const updatedFlights = [...flights];
        updatedFlights[index] = { ...updatedFlights[index], [field]: value };

        set({
          flights: updatedFlights,
          quoteData: {
            flightType,
            flights: updatedFlights,
          },
        });
      },

      removeFlightLeg: (index: number) => {
        const { flights, flightType } = get();
        if (flights.length > 1) {
          const updatedFlights = flights.filter((_, i) => i !== index);
          set({
            flights: updatedFlights,
            quoteData: {
              flightType,
              flights: updatedFlights,
            },
          });
        }
      },

      setIsSubmitting: (isSubmitting: boolean) => set({ isSubmitting }),

      setAirports: (airports: Airport[]) => set({ airports }),

      setAirportSuggestions: (airportSuggestions: Airport[]) =>
        set({ airportSuggestions }),

      setQuoteData: (quoteData: QuoteData | null) => set({ quoteData }),

      resetForm: () => {
        set({
          ...defaultState,
          quoteData: null,
        });
      },

      loadPersistedQuote: () => {
        const { quoteData } = get();
        if (quoteData) {
          set({
            flightType: quoteData.flightType,
            flights: quoteData.flights,
          });
        }
      },
    }),
    {
      name: "flight-store",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        flightType: state.flightType,
        flights: state.flights,
        quoteData: state.quoteData,
      }),
    }
  )
);
